import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, AlertTriangle, Search, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AbuserRecord {
  id: number;
  personId: number;
  agencyId: number;
  firstName: string | null;
  lastName: string | null;
  details: string | null;
  createdAt: string;
  matchType?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AbusersListProps {
  personId: string;
}

export function AbusersList({ personId }: AbusersListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingAbuser, setEditingAbuser] = useState<AbuserRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const { data: abusersData, isLoading } = useQuery<{ data: AbuserRecord[] }>({
    queryKey: ["abusers", personId],
    queryFn: () =>
      api.get<{ data: AbuserRecord[] }>(`/clients/${personId}/abusers`),
    enabled: !!personId,
  });

  const { data: searchResults } = useQuery<{ data: AbuserRecord[] }>({
    queryKey: ["abuserSearch", searchQuery],
    queryFn: () =>
      api.get<{ data: AbuserRecord[] }>("/abusers/search", {
        params: { q: searchQuery },
      }),
    enabled: searchQuery.length >= 2,
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      firstName: string;
      lastName: string;
      details?: string;
    }) => api.post(`/clients/${personId}/abusers`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["abusers", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      firstName?: string;
      lastName?: string;
      details?: string;
    }) => api.put(`/abusers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["abusers", personId] });
      setEditingAbuser(null);
    },
  });

  const createAlertMutation = useMutation({
    mutationFn: (data: {
      firstName?: string;
      lastName?: string;
      reason: string;
      severity?: string;
    }) => api.post("/alerts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  const abusers = abusersData?.data ?? [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Abuser Records</h2>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
            onClick={() => setShowSearch(!showSearch)}
          >
            <Search className="h-4 w-4" />
            Search Existing
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              setShowForm(true);
              setEditingAbuser(null);
            }}
          >
            <Plus className="h-4 w-4" />
            Add Abuser
          </Button>
        </div>
      </div>

      {/* Search panel */}
      {showSearch && (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-3">
              <label className="text-sm font-medium">
                Search existing abuser records
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {searchResults && searchResults.data.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {searchResults.data.length} result(s) found
                  </p>
                  {searchResults.data.map((result) => (
                    <div
                      key={result.id}
                      className="flex items-center justify-between rounded-md border p-3 text-sm"
                    >
                      <div>
                        <span className="font-medium">
                          {result.firstName} {result.lastName}
                        </span>
                        {result.matchType === "phonetic" && (
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                            Phonetic match
                          </span>
                        )}
                        {result.details && (
                          <p className="mt-1 text-muted-foreground">
                            {result.details}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() =>
                          createAlertMutation.mutate({
                            firstName: result.firstName ?? undefined,
                            lastName: result.lastName ?? undefined,
                            reason: `Matched abuser record: ${result.firstName} ${result.lastName}`,
                            severity: "warning",
                          })
                        }
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Create Alert
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit form */}
      {(showForm || editingAbuser) && (
        <Card>
          <CardContent className="p-4">
            <AbuserForm
              initial={editingAbuser}
              onSubmit={(data) => {
                if (editingAbuser) {
                  updateMutation.mutate({ id: editingAbuser.id, ...data });
                } else {
                  createMutation.mutate(data);
                }
              }}
              onCancel={() => {
                setShowForm(false);
                setEditingAbuser(null);
              }}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && abusers.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No abuser records for this client.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Abuser cards */}
      {!isLoading &&
        abusers.map((abuser) => (
          <Card key={abuser.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">
                    {abuser.firstName} {abuser.lastName}
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    Added{" "}
                    {new Date(abuser.createdAt).toLocaleDateString()}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditingAbuser(abuser)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() =>
                      createAlertMutation.mutate({
                        firstName: abuser.firstName ?? undefined,
                        lastName: abuser.lastName ?? undefined,
                        reason: `Alert for abuser: ${abuser.firstName} ${abuser.lastName}`,
                        severity: "warning",
                      })
                    }
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Set Alert
                  </Button>
                </div>
              </div>
            </CardHeader>
            {abuser.details && (
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {abuser.details}
                </p>
              </CardContent>
            )}
          </Card>
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline form
// ---------------------------------------------------------------------------

interface AbuserFormProps {
  initial?: AbuserRecord | null;
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    details?: string;
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function AbuserForm({ initial, onSubmit, onCancel, isLoading }: AbuserFormProps) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? "");
  const [lastName, setLastName] = useState(initial?.lastName ?? "");
  const [details, setDetails] = useState(initial?.details ?? "");

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium">
        {initial ? "Edit Abuser Record" : "Add Abuser Record"}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            First Name
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Last Name
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Details</label>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
          placeholder="Description, identifying information..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => onSubmit({ firstName, lastName, details: details || undefined })}
          loading={isLoading}
          disabled={!firstName.trim() || !lastName.trim()}
        >
          {initial ? "Save Changes" : "Add Abuser"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
